import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import post from '../utils/post.js';
import formatMoney from '../utils/formatMoney.js';
import AppHeader from '../components/AppHeader.js';
import Search from '../components/Search.js';
import UserQR from './UserQR.js';

const EMPTY_DATA = {
	perm0: [],
	perm1: [],
	NOT_PAYED_ADD_PERSON_DETAIL: '',
};

const SUMMARY_FILTERS = {
	WITHOUT_PERMANENT: 'without-permanent',
	WITH_PERMANENT: 'with-permanent',
	UNPAID_VISITS: 'unpaid-visits',
	TO_PAY: 'to-pay',
	OVERPAY: 'overpay',
	MESSAGE: 'message',
};

function toInteger(value) {
	return parseInt(value, 10) || 0;
}

function toNumber(value) {
	return Number(value) || 0;
}

function getMemberName(line) {
	if (line.NICKNAME !== '') {
		return `${line.LASTNAME} ${line.FIRSTNAME} (${line.NICKNAME})`;
	}

	return `${line.LASTNAME} ${line.FIRSTNAME}`;
}

function matchesSearch(line, searchValue) {
	const normalizedSearch = searchValue.trim().toLowerCase();

	if (normalizedSearch === '') {
		return true;
	}

	const phoneValue = line.PHONES || line.PHONE || '';
	const searchableValues = [line.EMAIL, line.FIRSTNAME, `${line.FIRSTNAME} ${line.LASTNAME}`, `${line.LASTNAME} ${line.FIRSTNAME}`, line.LASTNAME, line.NICKNAME, phoneValue];

	return searchableValues.some((value) =>
		String(value || '')
			.toLowerCase()
			.includes(normalizedSearch),
	);
}

function buildUnpaidVisitsSummary(items) {
	let totalCount = 0;
	let totalPrice = 0;

	(items || []).forEach((item) => {
		if (toInteger(item.ADD_PERSON_PAYED) === 0) {
			totalCount += toInteger(item.ADD_PERSON);
			totalPrice += toNumber(item.NOT_PAYED_PRICES);
		}

		if (toInteger(item.PAYED) === 0) {
			totalCount += 1;
			totalPrice += toNumber(item.NOT_PAYED_PRICES_USR);
		}
	});

	return {
		totalCount,
		totalPrice,
	};
}

function matchesSummaryFilter(line, sectionId, filterKey) {
	if (filterKey === null) {
		return true;
	}

	switch (filterKey) {
		case SUMMARY_FILTERS.WITHOUT_PERMANENT:
			return sectionId === 'perm0';
		case SUMMARY_FILTERS.WITH_PERMANENT:
			return sectionId === 'perm1';
		case SUMMARY_FILTERS.UNPAID_VISITS:
			return (line.not_payed_add_person || []).length > 0;
		case SUMMARY_FILTERS.TO_PAY:
			return toNumber(line.TOPAY_TOTAL) > 0;
		case SUMMARY_FILTERS.OVERPAY:
			return toNumber(line.TOPAY_TOTAL) < 0;
		case SUMMARY_FILTERS.MESSAGE:
			return line.MESSAGE !== '';
		default:
			return true;
	}
}

export default function Member({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState(EMPTY_DATA);
	const [showQR, setShowQR] = useState(false);
	const [qrInfo, setQrInfo] = useState({});
	const [searchValue, setSearchValue] = useState('');
	const [expandedRows, setExpandedRows] = useState({});
	const [activeSummaryFilter, setActiveSummaryFilter] = useState(null);

	useEffect(() => {
		let search = '';

		if (typeof window.location.pathname.split('/')[2] !== 'undefined') {
			search = decodeURI(window.location.pathname.split('/')[2]);
			setSearchValue(search);
		}

		getData();

		if (search !== '') {
			window.history.pushState({}, document.title, '/member');
		}
	}, []);

	const onChangeSearch = (value) => {
		setSearchValue(value);
		setExpandedRows({});
		setActiveSummaryFilter(null);
	};

	const getData = () => {
		setLoading(true);

		post('getListMember', {search: ''})
			.then((res) => {
				setData({...EMPTY_DATA, ...res.data});
				setExpandedRows({});
				setActiveSummaryFilter(null);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const getQRData = (id) => {
		setLoading(true);

		post('getQRData', {ID: id})
			.then((res) => {
				setQrInfo(res.data);
				setShowQR(true);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const closeQR = () => {
		setShowQR(false);
	};

	const toggleSummaryFilter = (filterKey) => {
		setActiveSummaryFilter((prevState) => (prevState === filterKey ? null : filterKey));
	};

	const toggleRow = (id) => {
		setExpandedRows((prevState) => ({
			...prevState,
			[id]: !prevState[id],
		}));
	};

	const renderStatusBadges = (line) => {
		const badges = [];

		if (line.MESSAGE !== '') {
			badges.push(
				<span className="member-page__badge member-page__badge--info" key="message">
					Zpráva
				</span>,
			);
		}

		if (toNumber(line.TOPAY_TOTAL) > 0) {
			badges.push(
				<span className="member-page__badge member-page__badge--danger" key="topay">
					{formatMoney(line.TOPAY_TOTAL)}
				</span>,
			);
		}

		if (toNumber(line.TOPAY_TOTAL) < 0) {
			const overpayAmount = Math.abs(toNumber(line.TOPAY_TOTAL));

			badges.push(
				<span className="member-page__badge member-page__badge--success" key="overpay">
					{formatMoney(overpayAmount)}
				</span>,
			);
		}

		if ((line.not_payed_add_person || []).length > 0) {
			const {totalPrice} = buildUnpaidVisitsSummary(line.not_payed_add_person || []);

			badges.push(
				<span className="member-page__badge member-page__badge--warning" key="unpaid-visits">
					{formatMoney(totalPrice)}
				</span>,
			);
		}

		/*
		if (line.DOORKEY !== '') {
			badges.push(
				<span className="member-page__badge member-page__badge--neutral" key="door-key">
					Klíč
				</span>
			);
		}
		*/

		return badges;
	};

	const renderUnpaidVisitsCard = (line, extraClassName = '') => {
		const unpaidVisits = line.not_payed_add_person || [];

		if (unpaidVisits.length === 0) {
			return null;
		}

		const {totalCount, totalPrice} = buildUnpaidVisitsSummary(unpaidVisits);
		const hasDetail = data.NOT_PAYED_ADD_PERSON_DETAIL !== '';

		return (
			<div className={`home-balance-card home-balance-card--warning member-page__detail-card member-page__detail-card--full${extraClassName !== '' ? ` ${extraClassName}` : ''}`} key={`unpaid-visits-${line.ID}`}>
				<div className="home-balance-card__header member-page__detail-header">
					<div className="home-balance-card__title-wrap">
						<div className="home-balance-card__title">Nezaplacené vstupy</div>
					</div>
					<div className="home-balance-card__columns" aria-hidden="true">
						<span>Já</span>
						<span>Doprovody</span>
					</div>
				</div>
				<div className="home-balance-card__body member-page__detail-card-body">
					{unpaidVisits.map((item, index) => {
						const isFuture = toInteger(item.IS_FUTURE) === 1;
						const isCompanionRow = toInteger(item.TYPE) === 2;

						return (
							<div className={`home-balance-row${isCompanionRow ? ' is-companion is-lock' : ' is-self'}`} key={`NOT_PAYED_ADD_PERSON${line.ID}-${index}`}>
								<div className="home-balance-row__main txt_l">
									<span className={`home-balance-row__title${isFuture ? ' is-future' : ''}`}>
										{item.RESERVATIONDTS} - {item.RESERVATIONLNG_H}h{item.ADD_PERSON > 0 ? ` + ${item.ADD_PERSON}x` : ''}
									</span>
									<div className="home-balance-row__amounts">
										<span className="home-balance-row__amount">{toInteger(item.PAYED) === 0 ? formatMoney(item.NOT_PAYED_PRICES_USR) : formatMoney(0)}</span>
										<span className="home-balance-row__amount home-balance-row__amount--accent">{item.ADD_PERSON > 0 ? formatMoney(item.NOT_PAYED_PRICES) : formatMoney(0)}</span>
									</div>
								</div>
							</div>
						);
					})}
				</div>
				{hasDetail ? <div className="home-balance-card__detail txt_l" dangerouslySetInnerHTML={{__html: data.NOT_PAYED_ADD_PERSON_DETAIL}}></div> : ''}
				<div className="home-balance-card__footer member-page__detail-footer">
					<span className="home-balance-card__total-label">Celkem</span>
					<span className="home-balance-card__total-value">
						{formatMoney(totalPrice)} ({totalCount}x)
					</span>
				</div>
			</div>
		);
	};

	const renderValueCard = (title, value, modifier, key, extraClassName = '') => {
		return (
			<div className={`home-balance-card member-page__detail-card ${modifier}${extraClassName !== '' ? ` ${extraClassName}` : ''}`} key={key}>
				<div className="home-balance-card__header member-page__detail-header member-page__detail-header--value">
					<div className="home-balance-card__title-wrap">
						<div className="home-balance-card__title">{title}</div>
					</div>
					<div className="member-page__metric-inline">{value}</div>
				</div>
			</div>
		);
	};

	const renderReservationsCard = (line, extraClassName = '') => {
		return (
			<div className={`home-balance-card member-page__detail-card${extraClassName !== '' ? ` ${extraClassName}` : ''}`} key={`reservations-${line.ID}`}>
				<div className="home-balance-card__header member-page__detail-header">
					<div className="home-balance-card__title-wrap">
						<div className="home-balance-card__title">Poslední vstupy</div>
					</div>
				</div>
				<div className="home-balance-card__body member-page__detail-card-body">
					{line.RESERVATIONS && line.RESERVATIONS.length > 0 ? (
						line.RESERVATIONS.map((reservation, index) => (
							<div className="member-page__detail-row" key={`reservation-${line.ID}-${index}`}>
								<span className="member-page__detail-row-title">{reservation.RESERVATIONDT}</span>
								<span className="member-page__detail-row-value">
									{reservation.RESERVATIONLNG_H}h{reservation.ADD_PERSON > 0 ? ` +${reservation.ADD_PERSON}x` : ''}
								</span>
							</div>
						))
					) : (
						<div className="member-page__detail-empty">Bez posledních vstupů.</div>
					)}
				</div>
			</div>
		);
	};

	const renderMessageCard = (line, extraClassName = '') => {
		if (line.MESSAGE === '') {
			return null;
		}

		return (
			<div className={`home-balance-card member-page__detail-card member-page__detail-card--muted${extraClassName !== '' ? ` ${extraClassName}` : ''}`} key={`message-${line.ID}`}>
				<div className="home-balance-card__header member-page__detail-header">
					<div className="home-balance-card__title-wrap">
						<div className="home-balance-card__title">Zpráva</div>
					</div>
				</div>
				<div className="home-balance-card__body member-page__detail-card-body member-page__detail-card-body--message">
					<div className="member-page__message" dangerouslySetInnerHTML={{__html: line.MESSAGE}}></div>
				</div>
			</div>
		);
	};

	const renderProfileCard = (line, toneClassName, extraClassName = '') => {
		return (
			<div className={`home-balance-card member-page__detail-card${extraClassName !== '' ? ` ${extraClassName}` : ''}`} key={`profile-${line.ID}`}>
				<div className="home-balance-card__header member-page__detail-header">
					<div className="home-balance-card__title-wrap">
						<div className="home-balance-card__title">Profil</div>
					</div>
				</div>
				<div className="home-balance-card__body member-page__detail-card-body member-page__detail-card-body--profile">
					<div className="member-page__detail-row member-page__detail-row--stacked">
						<a className={`member-page__contact-link ${toneClassName}`} href={`mailto:${line.EMAIL}?subject=Progress Gym&body=`}>
							{line.EMAIL}
						</a>
					</div>
					<div className="member-page__detail-row member-page__detail-row--stacked">
						<a className={`member-page__contact-link ${toneClassName}`} href={`tel:+${line.PHONES}`}>
							{line.PHONES}
						</a>
					</div>
					<div className="member-page__action-row">
						<Link className="member-page__action-button" to={`/payment/${line.LASTNAME} ${line.FIRSTNAME}`}>
							<img className="icon2 permanent2" alt="" />
						</Link>
						<Link className="member-page__action-button" to={`/eshopsysadmin/${line.LASTNAME} ${line.FIRSTNAME}`}>
							<img className="icon2 eshop active" alt="" />
						</Link>
						{usrInfo.usrId === 1 ? (
							<>
								<Link className="member-page__action-button" to={`/user/${line.LASTNAME} ${line.FIRSTNAME}`}>
									<img className="icon2 user" alt="" />
								</Link>
								<Link className="member-page__action-button" to={`/userlog/${line.EMAIL}`}>
									<img className="icon2 userlog" alt="" />
								</Link>
							</>
						) : (
							''
						)}
						<button className="member-page__action-button" type="button" onClick={() => getQRData(line.ID)}>
							<img alt="" className="icon2 qr" />
						</button>
					</div>
				</div>
			</div>
		);
	};

	const renderSectionList = (members, sectionKey) => {
		if (!members || members.length === 0) {
			return <div className="member-page__empty">V této sekci zatím nikdo není.</div>;
		}

		return members.map((line, index) => {
			const isExpanded = !!expandedRows[line.ID];
			const name = getMemberName(line);
			const hasPermanent = toInteger(line.PERNAMENTMEMBER) === 1;
			const toneClassName = hasPermanent && toInteger(line.PAYED_ORANGE) === 1 ? 'fnt_yellow' : '';
			const cardToneClassName = !hasPermanent && toInteger(line.REMOVE_USER) === 1 ? ' is-danger' : hasPermanent && toInteger(line.PAYED_ORANGE) === 1 ? ' is-warning' : '';
			const detailCards = [renderReservationsCard(line, 'member-page__detail-card--top'), renderProfileCard(line, toneClassName, 'member-page__detail-card--top')];

			if (toNumber(line.TOPAY_TOTAL) > 0) {
				detailCards.push(renderValueCard('Nezaplacený nákup', line.TOPAY_TOTALS, 'member-page__detail-card--danger', `eshop-topay-${line.ID}`, 'member-page__detail-card--full'));
			}

			if (toNumber(line.TOPAY_TOTAL) < 0) {
				const overpayAmount = Math.abs(toNumber(line.TOPAY_TOTAL));
				detailCards.push(renderValueCard('Předplaceno', formatMoney(overpayAmount), 'member-page__detail-card--success', `eshop-overpay-${line.ID}`, 'member-page__detail-card--full'));
			}

			const unpaidVisitsCard = renderUnpaidVisitsCard(line);
			if (unpaidVisitsCard) {
				detailCards.push(renderUnpaidVisitsCard(line, 'member-page__detail-card--full'));
			}

			const messageCard = renderMessageCard(line);
			if (messageCard) {
				detailCards.push(renderMessageCard(line, 'member-page__detail-card--full'));
			}

			return (
				<div className={`member-page__member-card${cardToneClassName}${isExpanded ? ' is-expanded' : ''}`} key={`${sectionKey}-${line.ID}`}>
					<button className="member-page__member-toggle" type="button" onClick={() => toggleRow(line.ID)}>
						<div className="member-page__member-main">
							<div className="member-page__member-name-row">
								<span className="member-page__member-index">{index + 1}.</span>
								<span className={`member-page__member-name ${toneClassName}`}>{name}</span>
							</div>
							<div className="member-page__member-meta">
								<span className={`member-page__member-activity${toInteger(line.LASTACTIVITY_IN) === 0 ? ' is-active' : ''}`}>{line.LASTACTIVITYDTS}</span>
								<span className="member-page__member-separator">•</span>
								<span className="member-page__member-visits">{line.USR_RECORDS}x</span>
							</div>
						</div>
						<div className="member-page__member-side">
							<div className="member-page__member-paidto">{line.PAYEDTO_DTS2}</div>
							<div className="member-page__member-badges">{renderStatusBadges(line)}</div>
						</div>
						<span className={`member-page__member-chevron${isExpanded ? ' is-expanded' : ''}`} aria-hidden="true"></span>
					</button>
					<div className={`member-page__detail-shell${isExpanded ? ' is-expanded' : ''}`}>
						<div className="member-page__detail-shell-in">
							<div className="member-page__detail-grid">{detailCards}</div>
						</div>
					</div>
				</div>
			);
		});
	};

	const sections = [
		{
			id: 'perm0',
			title: 'Bez permanentky',
			description: '',
			members: data.perm0 || [],
		},
		{
			id: 'perm1',
			title: 'S permanentkou',
			description: '',
			members: data.perm1 || [],
		},
	];

	const filteredSections = sections.map((section) => ({
		...section,
		members: (section.members || []).filter((line) => matchesSearch(line, searchValue) && matchesSummaryFilter(line, section.id, activeSummaryFilter)),
	}));

	let cntNotPayedAddPerson = 0;
	let cntMessage = 0;
	let cntToPay = 0;
	let cntOverPay = 0;

	sections.forEach((section) => {
		(section.members || []).forEach((line) => {
			if ((line.not_payed_add_person || []).length > 0) {
				cntNotPayedAddPerson++;
			}

			if (line.MESSAGE !== '') {
				cntMessage++;
			}

			if (toNumber(line.TOPAY_TOTAL) > 0) {
				cntToPay++;
			}

			if (toNumber(line.TOPAY_TOTAL) < 0) {
				cntOverPay++;
			}
		});
	});

	const summaryCards = [
		{
			id: SUMMARY_FILTERS.WITHOUT_PERMANENT,
			label: 'Bez per.',
			value: sections[0].members.length,
			toneClassName: 'member-page__summary-card--neutral',
		},
		{
			id: SUMMARY_FILTERS.WITH_PERMANENT,
			label: 'S per.',
			value: sections[1].members.length,
			toneClassName: 'member-page__summary-card--success',
		},
		{
			id: SUMMARY_FILTERS.UNPAID_VISITS,
			label: 'Vstupy',
			value: cntNotPayedAddPerson,
			toneClassName: 'member-page__summary-card--warning',
		},
		{
			id: SUMMARY_FILTERS.TO_PAY,
			label: 'Nezapl.',
			value: cntToPay,
			toneClassName: 'member-page__summary-card--danger',
		},
		{
			id: SUMMARY_FILTERS.OVERPAY,
			label: 'Přepl.',
			value: cntOverPay,
			toneClassName: 'member-page__summary-card--success',
		},
		{
			id: SUMMARY_FILTERS.MESSAGE,
			label: 'Zprávy',
			value: cntMessage,
			toneClassName: 'member-page__summary-card--info',
		},
	];

	return (
		<>
			<div className="page_main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
				<Search showSearch searchValue={searchValue} onChangeSearch={(e) => onChangeSearch(e)} debounceMs={0} />
				<UserQR showQR={showQR} closeQR={closeQR} qrInfo={qrInfo} />

				<div className={`page member-page ${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
					<div className="member-page__summary-panel">
						<div className="member-page__summary-grid">
							{summaryCards.map((card) => {
								const isActive = activeSummaryFilter === card.id;

								return (
									<button className={`home-summary-card member-page__summary-card ${card.toneClassName}${isActive ? ' is-active' : ''}`} key={card.id} type="button" aria-pressed={isActive} onClick={() => toggleSummaryFilter(card.id)}>
										<div className="home-summary-card__content">
											<span className="home-summary-card__eyebrow">{card.label}</span>
											<span className="home-summary-card__value">{card.value}</span>
										</div>
									</button>
								);
							})}
						</div>
					</div>

					<div className="member-page__content member-page__content--hidden-scrollbar">
						{filteredSections.map((section) => (
							<div className="member-page__section" key={section.id}>
								<div className="member-page__section-header">
									<div>
										<div className="member-page__section-title">{section.title}</div>
										<div className="member-page__section-text">{section.description}</div>
									</div>
									<div className="member-page__section-count">{section.members.length}</div>
								</div>
								<div className="member-page__section-list">{renderSectionList(section.members, section.id)}</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</>
	);
}
